import { sendCompanyMergeRequest } from "@/models/socket/company.model";
import { MergeCompaniesContext } from "@/services/MergeCompaniesContext/mergeCompaniesContext";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shadcn/components/ui/alert-dialog";
import { Button } from "@/shadcn/components/ui/button";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import React, { useContext, useState } from "react";

type MergeCompaniesSubmitConfirmationDialogProps = {};

const MergeCompaniesSubmitConfirmationDialog: React.FC<
  MergeCompaniesSubmitConfirmationDialogProps
> = () => {
  const {
    keepSideUser,
    removeSideUser,
    keepSideCompany,
    removeSideCompany,
    setSuccessfulSubmission,
  } = useContext(MergeCompaniesContext);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false); // Controls dialog visibility to allow programmatic close

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);

    // Prevents submission if required companies are not selected, to avoid invalid requests
    if (!keepSideCompany || !removeSideCompany) {
      console.error("Keep or remove side company is not set.");
      setError("Please select both companies to merge.");
      setSubmitting(false);
      return;
    }

    const response = await sendCompanyMergeRequest(
      keepSideCompany.company_id,
      removeSideCompany.company_id
    );

    if (response.rStatus === "success") {
      genericSuccessToast("Companies merged successfully");
      setOpen(false); // Closes dialog after successful submission to provide feedback and reset UI
      setSuccessfulSubmission(true); // Sets successful submission state to true
    } else {
      genericFailureToast(
        response.rData ||
          "Failed to submit company merge request. Please try again."
      );
    }
    setSubmitting(false); // Ensures button state resets after request completes
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          className="m-auto mt-8 w-auto"
          disabled={
            !keepSideUser ||
            !removeSideUser ||
            !keepSideCompany ||
            !removeSideCompany
          }
          size={"lg"}
          onClick={() => {
            setOpen(true); // Opens dialog on button click to allow confirmation
          }}
        >
          Submit Merge
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm Company Merge</AlertDialogTitle>
          <AlertDialogDescription></AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-4">
          <p>
            Are you sure you want to confirm the merge of the selected
            companies? This action cannot be undone.
          </p>
          {error && <div className="text-sm text-red-500">{error}</div>}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>
            Cancel
          </AlertDialogCancel>
          <Button className="" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting..." : "Confirm"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default MergeCompaniesSubmitConfirmationDialog;
